// janus-engine/ability/rolls/chain-leap.ts — the CHAIN / LEAP layers (minted 2026-10-07 for Geno's Psion v12 PDF — Pyrokinetic L6 menta
// "Chain Combustion": "On a hit, it takes 5d6 Fire damage, then flames leap to up to two other creatures within 15ft of it, dealing 2d6 Fire
// damage to each"). The BROWSER rule; its PHP copy is ft_chain_leap_*() (includes/functions/janus/rolls/chain_leap.php) — R-175.
//
//   chain_targets_max  (int) — how many OTHER creatures the effect may leap to (0 / absent = no chain)
//   chain_distance_ft  (int) — each leap target must be within this many feet of the PRIMARY target (rim distance, the front-end rule)
//   chain_dice / chain_die (int) — the leap damage dice; the damage type is the host's own (its first damage_type)
//
// RULE: the caster picks 1 + up to chain_targets_max creatures (pick order: the FIRST is the primary). Only the primary is contested; the
// leap targets are not. When the caster WINS the primary's contest (a tie wins) and its post-effect conditions are met, each leap target
// within chain_distance_ft of the primary takes chain_dice d chain_die of the host's damage type (its own resistances, mitigation …; no
// half-on-save, no status effects, no push). A lost contest (even a save-for-half one) leaps nowhere. Leap targets resolve after the
// primary, in pick order. Only a Contesting Roll host chains; any other roll type carrying the layers hands back.
//
// Browser seams: the picker cap (shared/ability-targeting-contract.ts), the count ceiling + chainGeometryVerdict (actions/cast-range-gate.ts,
// wired in encounter-overlays/use-ability.ts), and the resolution (contesting-roll.ts → chainLeapDamage below).
import { type JanusCtx, type Row, ecId, ecLean, handBack, upper } from '../context.js';
import { type JanusInput } from '../janus.js';
import { damageTypeRoll } from './damage-type-roll.js';
import { pageDistanceFt } from '../../reactions/page-trigger-readers.js';
import { TriggerInputUnavailable } from '../../reactions/trigger-dispatch.js';

export const CHAIN_TARGETS_MAX = 'chain_targets_max';
export const CHAIN_DISTANCE_FT = 'chain_distance_ft';
export const CHAIN_DICE = 'chain_dice';
export const CHAIN_DIE = 'chain_die';
export const CHAIN_LAYERS = [CHAIN_TARGETS_MAX, CHAIN_DISTANCE_FT, CHAIN_DICE, CHAIN_DIE] as const;
export const CONTESTING_ROLL = 'AD80D31D-898B-66CE-0894-1335D11B1529';

export interface ChainSpec { max: number; distanceFt: number; dice: number; die: number }

const intOf = (value: unknown): number => {
  let v = value;
  while (Array.isArray(v)) v = v[0];
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

/** The host's chain, or null when it has none (chain_targets_max < 1). A malformed chain (no dice / die) is a chain of 0 dice — still a
 *  chain for targeting, so the picks are never resolved as ordinary extra targets. */
export function chainSpec(read: { value: (row: Row | null, key: string) => unknown }, ability: Row | null): ChainSpec | null {
  if (!ability) return null;
  const max = intOf(read.value(ability, CHAIN_TARGETS_MAX));
  if (max < 1) return null;
  return { max, distanceFt: Math.max(0, intOf(read.value(ability, CHAIN_DISTANCE_FT))), dice: Math.max(0, intOf(read.value(ability, CHAIN_DICE))),
    die: Math.max(0, intOf(read.value(ability, CHAIN_DIE))) };
}

/** The leap fragment: the host with its dice swapped for the chain dice, its flat mod and upcast growth dropped. Same item id (the log row
 *  names the host). */
export function chainLeapAbility(ability: Row, spec: ChainSpec): Row {
  const copy = JSON.parse(JSON.stringify(ability)) as Row;
  const layers = (copy.item_layer_types && typeof copy.item_layer_types === 'object' && !Array.isArray(copy.item_layer_types)
    ? copy.item_layer_types : (copy.item_layer_types = {})) as Row;
  layers.dice = spec.dice;
  layers.die = spec.die;
  for (const key of ['mod', 'dice_upcast', 'die_upcast', 'mod_upcast', ...CHAIN_LAYERS]) delete layers[key];
  return copy;
}

/** Leap damage onto `leaps` after the primary's contest LANDED. Pick order; a leap target out of range / dead / the primary again is skipped. */
export function chainLeapDamage(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, primary: Row, leaps: Row[], spec: ChainSpec): void {
  if (spec.dice < 1 || spec.die < 1 || leaps.length === 0) return;
  // upcast_roll() in damage_type_roll reads the CAST item's dice_upcast / mod_upcast — the leap fragment must not grow with it (PHP drops it too).
  if (ctx.castLevel != null) {
    const cast = ctx.read.definition(upper(input.abilityId)) ?? ability;
    if (ctx.read.has(cast, 'dice_upcast') || ctx.read.has(cast, 'mod_upcast')) handBack('chain_leap:upcast');
  }
  const leapAbility = chainLeapAbility(ability, spec);
  const host = { value: (row: Row | null, key: string) => ctx.read.value(row, key), entries: (row: Row | null, key: string) => ctx.read.entries(row, key) };
  const seen = new Set<string>([ecId(primary)]);
  for (const leap of leaps) {
    const id = ecId(leap);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (Number(ecLean(leap, 'encounter_creature_dead') ?? 0) === 1) continue;
    let distance: number | null;
    try { distance = pageDistanceFt(host, primary, leap); }
    catch (error) { if (error instanceof TriggerInputUnavailable) handBack(`chain_leap:${error.message}`); throw error; }
    if (distance === null) handBack('chain_leap:distance-unknown');
    if (distance! > spec.distanceFt) continue;
    damageTypeRoll(ctx, input, leapAbility, caster, leap);
  }
}
